import { useState } from 'react';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { EMPLOYEE_KIND_LABEL } from '@masamune/shared';
import { useModalStore } from '../../stores/modal.store';
import { isoDay } from '../../features/admin/adminApi';
import { useDipendentiActions, type EmployeeKind, type Persona } from '../../features/admin/dipendenti/dipendentiApi';

/**
 * Scheda di una persona in busta paga (Amministrazione › Dipendenti).
 *
 * Tre usi, un solo modale:
 *  - `nuova`     "+ Nuova persona in busta paga";
 *  - `modifica`  la matita sulla riga;
 *  - `termina`   "Termina il rapporto": solo la data, e cosa succede dopo.
 *
 * I nomi in banca sono il pezzo che conta: il riconoscimento delle paghe
 * collega un bonifico a una persona solo se la controparte e' scritta
 * esattamente cosi' (maiuscole a parte). Si salva con Ctrl/Cmd+Invio.
 */

export type SchedaPersonaMode = 'nuova' | 'modifica' | 'termina';

export function apriSchedaPersona(employee?: Persona, mode: SchedaPersonaMode = employee ? 'modifica' : 'nuova') {
  useModalStore.getState().open('newEmployee', { employee, mode });
}

/** I tipi di rapporto con i nomi di @masamune/shared (gli stessi della tabella in Dipendenti). */
const RAPPORTI: EmployeeKind[] = ['DIPENDENTE', 'APPRENDISTA', 'AMMINISTRATORE'];

export function NewEmployeeModal({ onClose, employee, mode = employee ? 'modifica' : 'nuova' }: {
  onClose: () => void;
  employee?: Persona;
  mode?: SchedaPersonaMode;
}) {
  const { salva, termina } = useDipendentiActions();
  const [form, setForm] = useState({
    fullName: employee?.fullName ?? '',
    kind: (employee?.kind ?? 'DIPENDENTE') as EmployeeKind,
    partTimePct: employee?.partTimePct != null ? String(employee.partTimePct) : '',
    hiredAt: employee?.hiredAt ?? '',
    endedAt: employee?.endedAt ?? '',
    bankNames: (employee?.bankNames ?? []).join('\n'),
    tfrRef: employee?.tfrRef ?? '',
    note: employee?.note ?? '',
  });
  const [fine, setFine] = useState(employee?.endedAt ?? isoDay());

  const pct = form.partTimePct.trim() ? Number(form.partTimePct.replace(',', '.')) : null;
  const pctOk = pct == null || (Number.isInteger(pct) && pct >= 1 && pct <= 100);
  const nomiBanca = form.bankNames.split('\n').map((s) => s.trim()).filter(Boolean);
  const pending = salva.isPending || termina.isPending;

  const canSubmit = mode === 'termina'
    ? !!fine && !pending
    : form.fullName.trim().length >= 2 && pctOk && !pending;

  const submit = () => {
    if (!canSubmit) return;
    if (mode === 'termina' && employee) {
      termina.mutate({ id: employee.id, endedAt: fine, nome: employee.fullName }, { onSuccess: onClose });
      return;
    }
    salva.mutate(
      {
        id: employee?.id,
        data: {
          fullName: form.fullName.trim(),
          kind: form.kind,
          partTimePct: pct,
          hiredAt: form.hiredAt || null,
          endedAt: form.endedAt || null,
          bankNames: nomiBanca,
          tfrRef: form.tfrRef.trim() || null,
          note: form.note.trim() || null,
        },
      },
      { onSuccess: onClose },
    );
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
  };

  if (mode === 'termina' && employee) {
    return (
      <Modal
        title={`Fine del rapporto di ${employee.fullName}`}
        kicker="Dipendenti"
        onClose={onClose}
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <div className="spacer" />
            <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}>Termina il rapporto</button>
          </>
        }
      >
        <div className="form-grid" onKeyDown={onKeyDown}>
          <Field label="Ultimo giorno di lavoro" required span={6}>
            <input className="inp mono" type="date" autoFocus value={fine} onChange={(e) => setFine(e.target.value)} />
          </Field>
          <div className="text-[13px] mute" style={{ gridColumn: 'span 12', lineHeight: 1.5 }}>
            Dopo questa data {employee.fullName} esce dalle persone in busta paga. Lo stipendio dell'ultimo mese e il TFR
            si pagano il mese dopo: restano nella previsione fino ad allora, poi le spese fisse si fermano da sole.
            I pagamenti già fatti restano nello storico.
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={mode === 'modifica' && employee ? `Scheda di ${employee.fullName}` : 'Nuova persona in busta paga'}
      kicker="Dipendenti"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {mode === 'modifica' ? 'Salva la scheda' : 'Aggiungi la persona in busta paga'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Nome e cognome" required span={12}>
          <input className="inp" autoFocus placeholder="Es. Ylenia Gentile" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <Field label="Tipo di rapporto" span={7}>
          <select className="inp" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as EmployeeKind })}>
            {RAPPORTI.map((r) => <option key={r} value={r}>{EMPLOYEE_KIND_LABEL[r]}</option>)}
          </select>
        </Field>
        <Field label="Part time, in percentuale" span={5} hint={pctOk ? 'Vuoto se è a tempo pieno' : 'Un numero intero da 1 a 100'}>
          <input className="inp mono" inputMode="numeric" placeholder="Es. 50" value={form.partTimePct} onChange={(e) => setForm({ ...form, partTimePct: e.target.value })} />
        </Field>
        <Field label="Data di assunzione" span={6}>
          <input className="inp mono" type="date" value={form.hiredAt} onChange={(e) => setForm({ ...form, hiredAt: e.target.value })} />
        </Field>
        {mode === 'modifica' && (
          <Field label="Ultimo giorno di lavoro" span={6} hint="Vuoto finché il rapporto è in corso">
            <input className="inp mono" type="date" value={form.endedAt} onChange={(e) => setForm({ ...form, endedAt: e.target.value })} />
          </Field>
        )}
        <Field
          label="Nomi con cui compare nei bonifici"
          span={12}
          hint="Uno per riga, scritti come nella banca (es. Gentile Ylenia Federica). Servono a riconoscere stipendi e rimborsi: senza, i bonifici non si collegano alla persona."
        >
          <textarea className="inp" rows={3} value={form.bankNames} onChange={(e) => setForm({ ...form, bankNames: e.target.value })} />
        </Field>
        <Field label="Numero della polizza TFR (Alleanza)" span={12} hint="Il numero che compare nella causale del bonifico ad Alleanza: serve a riconoscere il TFR di questa persona.">
          <input className="inp mono" placeholder="Es. 26378822" value={form.tfrRef} onChange={(e) => setForm({ ...form, tfrRef: e.target.value })} />
        </Field>
        <Field label="Note" span={12}>
          <textarea className="inp" rows={2} placeholder="Date importanti, accordi, cose da ricordare" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
